import { bridgeCallFunc } from '@/utils/instantLoaded/tools';
import { agentStore, initStore, userStore } from '@/stores/index';
import { getImageFileFromBase64, getTrans } from './tools';
import { MediaConstants, VideoTransifyKey, isAndroidReg, ToastMediaErrorTime, GetMsgResCode, TRACKING_OPERATION, TRACKING_TARGET, ReportLabel } from './constants';
import { CustomUploadStatus, UploadStatus, VideoFailedReason } from '@/types/components/Video';
import { batchPostChatMsg, reviewChatEndText } from '@/utils/agent';
import { getVideoToken } from '@/services/chat';
import { dispatchTrack } from './tracking';
import { UploadFileStatus } from '@/stores/agent';
import { isObject } from '@/utils/lodashReplacement';
// import transform from 'lodash/transform';
// import isObject from 'lodash/isObject';
import * as Toast from '@/utils/Toast';
import reporter from '@/utils/report';
import { getConfig } from '@/config/core';

export interface VideoParams {
  thumbnail: string;
  id: string;
  videoDuration: number;
  width: number;
  height: number;
}

/**
 * 选取视频回调
 */
export const handleSelectVideo = ({ thumbnail, id, videoDuration, width, height }: VideoParams) => {
  try {
    const image = getImageFileFromBase64(thumbnail);

    const curPos = agentStore.ADD_AGENT_DATA_ITEM({
      from: 'user',
      type: 'Video',
      data: {
        thumbnail: window.URL.createObjectURL(image),
        vid: id,
        videoDuration,
        status: UploadStatus.Compressing,
        width,
        height,
      },
    });

    if (curPos) {
      agentStore.updateMediasIdMapPosition(id, curPos);
      agentStore.UPDATE_AGENT_DATA_ITEM({ curPos }, curPos);

      // 如果会话已经结束
      if (agentStore.agentEndedReason) {
        // 回显agent离开文案
        reviewChatEndText();
        agentStore.UPDATE_AGENT_DATA_ITEM({ status: UploadStatus.SessionEnd }, curPos);
        return;
      }
    }
  } catch (e) { }
};

// 播放视频
export const playVideo = (src: string) => {
  if (!src) { return; }

  bridgeCallFunc(() => {
    initStore.UPDATE_DONT_SCROLL_TO_BOTTOM(true);

    window.bridgeCallHandler(
      'fullScreenImage',
      {
        medias: [
          {
            mediaUrl: src,
            type: 1,
          },
        ],
      },
      () => { },
    );
  });
};

// 开始上传
export const startMediaUpload = (id: string, token: string) => {
  // 如果会话没有结束
  if (!agentStore.agentEndedReason) {
    const { media } = getConfig();
    bridgeCallFunc(() => {
      window.bridgeCallHandler('mediaStartUploadWithId', {
        id,
        tokenForUpload: token,
        ...media,
      });
    });
  } else {
    // 会话结束了
    updateVideoStatus(id, UploadStatus.SessionEnd);
  }
};

// 取消上传
export const cancelMediaUpload = (ids: Array<string>) => {
  bridgeCallFunc(() => {
    window.bridgeCallHandler('mediaCancelUploadWithIds', { ids }, function () { });
  });
};

// 格式化从后端获取的 token 和 vid
export const formatMediaTokens = ({ vids, ...rest }: { vids: Array<string> }) => {
  return (vids || []).map(vid => {
    return JSON.stringify(replaceUnderscore({ vid, ...rest }));
  });
};

// 去掉参数 key 中的下划线
export const replaceUnderscore = (obj: any) => {
  // return transform(obj, function (result, value, key) {
  //   let currentKey = key;

  //   if (typeof key === 'string') {
  //     currentKey = key.replace(/\_/g, '');
  //   }

  //   // @ts-ignore
  //   result[currentKey] = isObject(value) ? replaceUnderscore(value) : value;
  // });
  if (isObject(obj)) {
    const res: any = Array.isArray(obj) ? [] : {};
    for (const key of Reflect.ownKeys(obj)) {
      let currentKey = key;

      if (typeof key === 'string') {
        currentKey = key.replace(/\_/g, '');
      }

      res[currentKey] = isObject(obj[key]) ? replaceUnderscore(obj[key]) : obj[key];
    }
    return res;
  }
  return obj;
};

// 修改 video 的状态
export const updateVideoStatus = (vid: string, status: UploadStatus) => {
  const curPos = agentStore.mediasIdMapPosition[vid];

  agentStore.UPDATE_AGENT_DATA_ITEM({ status }, curPos);
};

// 批量修改 video 状态
export const batchUpdateVideoStatus = (vids: Array<string>, status: UploadStatus) => {
  vids.forEach((vid) => {
    updateVideoStatus(vid, status);
  });
};

// 本地路径 添加file协议
export const addFileProtocol = (path: string) => {
  if (!path) { return; }

  if (!path.startsWith('file://')) {
    return `file://${path}`;
  }

  return path;
};

// 对于 cdn 视频链接 添加 url 参数
export const addExtraParamsForPlayVideo = (path: string) => {
  if (!path) { return; }

  const { userAgent } = navigator;
  const isAndroid = isAndroidReg.test(userAgent);

  // 对于安卓机，需要添加 type=mp4 才可以正常播放
  if (isAndroid) {
    const hasParamsInPath = path.indexOf('?') !== -1;

    return hasParamsInPath ? `${path}&type=mp4` : `${path}?type=mp4`;
  }

  return path;
};

// 获取token失败 重试上传
export const retryForGetTokenFailed = (id: string) => {
  // 修改状态为压缩中
  updateVideoStatus(id, UploadStatus.Compressing);
  // 上传中加1
  agentStore.UPDATE_FILE_UPLOADING_COUNT(UploadFileStatus.UPLOADING);
  // 获取token
  getVideoToken(agentStore.sessionKey, 1)
    .then(res => {
      handleVideoTokenResponse(res, [id]);
    })
    .catch(() => {
      updateVideoStatus(id, UploadStatus.GetTokenFailed);
    });
};

// 上传失败 重试上传
export const retryForUploadFailed = (id: string) => {
  // 修改状态为压缩中
  updateVideoStatus(id, UploadStatus.Compressing);
  // 上传中加1
  agentStore.UPDATE_FILE_UPLOADING_COUNT(UploadFileStatus.UPLOADING);
  // 获取token
  getVideoToken(agentStore.sessionKey, 1)
    .then(res => {
      handleVideoTokenResponse(res, [id]);
    })
    .catch(() => {
      updateVideoStatus(id, UploadStatus.GetTokenFailed);
    });
};

// 压缩失败 给提示
export const retryForCompressFailed = () => {
  toastError();
};

// 发送消息失败 重发消息
export const retryForSendingMessageFailed = (vid: string, params: any) => {
  updateVideoStatus(vid, UploadStatus.SendingMessage);
  sendVideoMessage(vid, params);
};

// toast 提示 上传错误
export const toastError = () => {
  bridgeCallFunc(() => {
    Toast.error(getTrans(VideoTransifyKey.VideoUploadingFailed));
  });
};

// 处理 获取token接口响应
export const handleVideoTokenResponse = (res: any, videoIds: Array<string>) => {
  if (!(res instanceof Error)) {
    const formatTokens = formatMediaTokens(res.data);
    const { vids: mmsIds } = res.data;

    videoIds.forEach((videoId, index) => {
      const curPos = agentStore.mediasIdMapPosition[videoId];

      // video native 上传
      startMediaUpload(videoId, formatTokens[index]);
      // 更新
      agentStore.UPDATE_AGENT_DATA_ITEM({ mmsId: mmsIds[index] }, curPos);
    });
  } else {
    batchUpdateVideoStatus(videoIds, UploadStatus.GetTokenFailed);
  }
};

// 限制弹出提示的次数
export const toastErrorLimit = (() => {
  let lastToastTime: any = 0;

  return () => {
    const currentTime = Date.now();

    // 每 ToastMediaErrorTime 毫秒 最多显示一次
    if (currentTime - lastToastTime > ToastMediaErrorTime) {
      toastError();
      lastToastTime = currentTime;
    }
  };
})();

// 发送 video url 给sf
export const sendVideoMessage = (vid: string, params: any) => {
  batchPostChatMsg(
    agentStore.sessionKey,
    params,
    { vid },
    ({ code, data }, extraParams) => {
      const curPos = agentStore.mediasIdMapPosition[extraParams.vid];
      const curData = agentStore.GET_AGENT_DATA_ITEM(curPos);
      const isSuccess = code === GetMsgResCode.SUCCESS;

      trackVideoFinish({
        code,
        isUploadSuccess: true,
        isSendSuccess: isSuccess,
        duration: curData.data.videoDuration,
        uploadDuration: curData.data.uploadCostTime,
        messageId: data?.message_id,
        failureReason: isSuccess ? undefined : VideoFailedReason[UploadStatus.SendMessageFailed],
      });

      agentStore.UPDATE_AGENT_DATA_ITEM({ beErrorCode: code || undefined }, curPos);

      if (code === GetMsgResCode.SUCCESS) {
        agentStore.UPDATE_AGENT_DATA_ITEM({
          messageId: data?.message_id,
          status: UploadStatus.SendMessageSucceed,
        }, curPos);
      } else {
        updateVideoStatus(extraParams.vid, UploadStatus.SendMessageFailed);
        toastErrorLimit();
      }
    },
  );
};

export const trackImageFinish = ({ isUploadSuccess, isSendSuccess, messageId }: { isUploadSuccess: boolean, isSendSuccess: boolean, messageId?: string }) => {
  const { loyaltyTierCode } = userStore;

  dispatchTrack({
    operation: TRACKING_OPERATION.ACTION_FINISH_UPLOAD_SEND,
    target: TRACKING_TARGET.image,
    data: {
      client_rank: loyaltyTierCode?.toString() || '',
      is_upload_success: isUploadSuccess ? 1 : 0,
      is_send_success: isSendSuccess ? 1 : 0,
      message_id: messageId || '0',
    },
  });
};

export const trackVideoFinish = (
  {
    isUploadSuccess, duration,
    uploadDuration, isSendSuccess,
    messageId, code, failureReason,
  }: {
    isUploadSuccess: boolean;
    isSendSuccess: boolean;
    duration: number;
    uploadDuration?: number;
    messageId?: string;
    code?: number;
    failureReason?: number;
  },
) => {
  const { loyaltyTierCode } = userStore;

  dispatchTrack({
    operation: TRACKING_OPERATION.ACTION_FINISH_UPLOAD_SEND,
    target: TRACKING_TARGET.video,
    data: {
      client_rank: loyaltyTierCode?.toString() || '',
      is_upload_success: +isUploadSuccess,
      upload_duration: uploadDuration,
      video_original_duration: duration,
      message_id: messageId || '0',
      is_send_success: +isSendSuccess,
      failure_reason: failureReason,
      be_error_code: code || undefined,
      current_chat_session_id: initStore.sessionId || '0',
    },
  });
};

export const handleVideoUploadFinishReport = (status: UploadStatus) => {
  let label = '';

  switch (status) {
    case UploadStatus.CompressFailed:
      label = ReportLabel.Video.VideoFailedByCompressFailed;
      break;
    case UploadStatus.GetTokenFailed:
      label = ReportLabel.Video.VideoFailedByGetTokenFailed;
      break;
    case UploadStatus.UploadFailed:
      label = ReportLabel.Video.VideoFailedByUploadFailed;
      break;
    case UploadStatus.SendMessageFailed:
      label = ReportLabel.Video.VideoFailedBySendMessageFailed;
      break;
    case UploadStatus.SessionEnd:
      label = ReportLabel.Video.VideoFailedBySessionEnd;
      break;
    case UploadStatus.UploadSucceed:
      label = ReportLabel.Video.VideoUploadSucceed;
      break;
    default:
      break;
  }

  if (!label) {
    return;
  }

  reporter.apiBatchReport({ type: 'count', value: { label }});
};
